import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  Plus,
  Upload,
  Search,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Shield,
  Mail,
  Building2,
  Filter,
  MoreVertical,
  Eye,
  Lock,
  Key,
  Unlock,
  Phone,
  CheckCircle,
  XCircle,
  X,
  EyeOff,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import backendApi from '../../services/backendApi';
import {
  getFallbackOnboardingSchema,
  isDemoTemplateRow,
  OnboardingSchemaMap,
  parseBooleanCell,
  parseExcelRows,
  pickRowValue,
  validateOnboardingRow,
} from '../../utils/adminBulkOnboarding';
import { DeletionProgressPopup } from '../../components/common/DeletionProgressPopup';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  role_name?: string; // New role system
  role_display?: string; // New role system
  is_active: boolean;
  avatar?: string;
  profile_picture?: string;
  country_code?: string | null;
  phone_number?: string | null;
  site?: {
    site_id?: number;
    site_name: string;
    site_code: string;
    site_type?: string;
    company_name?: string;
    company_id?: number;
  };
  company_id?: number | string | null;
  company_name?: string | null;
  // ... other fields
  created_at?: string;
  last_login?: string;
  account_locked_until?: string;
  failed_login_attempts?: number;
}

interface CompanyOption {
  company_id: number | string;
  company_name: string;
  company_code?: string;
}

interface SiteOption {
  site_id: number | string;
  site_name: string;
  site_code: string;
  company_id?: number | string | null;
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const normalizeRole = (role?: string | null, hasSite?: boolean | null) => {
    const value = String(role || '').trim().toLowerCase().replace(' ', '_');
    if (!value) return '';
    if (value === 'company_admin') return 'ho_admin';
    if (value === 'user' || value === 'viewer') return 'general_user';
    if (value === 'admin') return hasSite ? 'site_admin' : 'ho_admin';
    return value;
  };

  // Role detection
  const roleName = normalizeRole(String(currentUser?.roleName || currentUser?.role || ''), !!currentUser?.siteCode);
  const isSystemAdmin = !!currentUser?.isGlobalAdmin || !!(currentUser as any)?.is_superuser || ['system_admin', 'super_admin'].includes(roleName);
  const isHoAdmin = roleName === 'ho_admin' || roleName === 'company_admin' || (roleName === 'admin' && !currentUser?.siteCode);
  const isSiteAdmin = roleName === 'site_admin' || (roleName === 'admin' && !!currentUser?.siteCode);

  const [feedbackToast, setFeedbackToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [resetModal, setResetModal] = useState<{ isOpen: boolean; userId: number | null; username: string }>({
    isOpen: false,
    userId: null,
    username: ''
  });
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [showResetPwd, setShowResetPwd] = useState(false);

  // Permission helpers
  // Site Admin: can only create general_user
  // HO Admin: can create ho_admin, site_admin, general_user
  // System Admin: can create all roles
  const canCreateUser = isSystemAdmin || isHoAdmin || isSiteAdmin;
  const currentUserId = Number((currentUser as any)?.userId || sessionStorage.getItem('user_id') || 0);
  const isSelfUser = (targetUser: User): boolean => {
    const targetId = Number(targetUser.id || 0);
    if (currentUserId > 0 && targetId > 0 && targetId === currentUserId) {
      return true;
    }

    const currentUsername = String(currentUser?.username || '').trim().toLowerCase();
    const targetUsername = String(targetUser.username || '').trim().toLowerCase();
    if (currentUsername && targetUsername && currentUsername === targetUsername) {
      return true;
    }

    const currentEmail = String(currentUser?.email || '').trim().toLowerCase();
    const targetEmail = String(targetUser.email || '').trim().toLowerCase();
    return !!(currentEmail && targetEmail && currentEmail === targetEmail);
  };

  // Can edit/delete: cannot edit/delete users of equal or higher role
  const canManageUser = (targetUser: User): boolean => {
    const targetRole = normalizeRole(targetUser.role_name || targetUser.role || '', !!targetUser.site?.site_id);
    if (isSystemAdmin) return true;
    if (isHoAdmin) return ['ho_admin', 'company_admin', 'site_admin', 'general_user', 'user'].includes(targetRole);
    if (isSiteAdmin) return ['general_user', 'user'].includes(targetRole);
    return false;
  };
  const canViewOrEditUser = (targetUser: User): boolean => isSelfUser(targetUser) || canManageUser(targetUser);
  const canResetPasswordForUser = (targetUser: User): boolean => isSelfUser(targetUser) || canManageUser(targetUser);
  const canUnlockUser = (targetUser: User): boolean => !isSelfUser(targetUser) && canManageUser(targetUser) && isAccountLocked(targetUser);
  const canToggleUserStatus = (targetUser: User): boolean => !isSelfUser(targetUser) && canManageUser(targetUser);
  const canDeleteUser = (targetUser: User): boolean => !isSelfUser(targetUser) && canManageUser(targetUser);

  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [sites, setSites] = useState<SiteOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingScopeFilters, setLoadingScopeFilters] = useState(false);
  const [deleteStreamUrl, setDeleteStreamUrl] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState<string>(() => {
    try {
      const stored = localStorage.getItem('admin_users_company_filter');
      if (stored) return stored;
    } catch (e) {}
    return 'all';
  });
  const [siteFilter, setSiteFilter] = useState<string>(() => {
    try {
      const stored = localStorage.getItem('admin_users_site_filter');
      if (stored) return stored;
    } catch (e) {}
    return 'all';
  });
  const [roleFilter, setRoleFilter] = useState<string>(() => {
    try {
      const stored = localStorage.getItem('admin_users_role_filter');
      if (stored) return stored;
    } catch (e) {}
    return 'all';
  });
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    try {
      const stored = localStorage.getItem('admin_users_status_filter');
      if (stored) return stored;
    } catch (e) {}
    return 'all';
  });

  useEffect(() => {
    try { localStorage.setItem('admin_users_company_filter', companyFilter); } catch (e) {}
  }, [companyFilter]);

  useEffect(() => {
    try { localStorage.setItem('admin_users_site_filter', siteFilter); } catch (e) {}
  }, [siteFilter]);

  useEffect(() => {
    try { localStorage.setItem('admin_users_role_filter', roleFilter); } catch (e) {}
  }, [roleFilter]);

  useEffect(() => {
    try { localStorage.setItem('admin_users_status_filter', statusFilter); } catch (e) {}
  }, [statusFilter]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [actionMenuOpen, setActionMenuOpen] = useState<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedScopedFilterFetch = useRef(false);
  const POLL_INTERVAL = 30000; // 30s scalable polling (prevents DB connection exhaustion)
  const [scopeFiltersReady, setScopeFiltersReady] = useState(false);
  const [isUploadingUsers, setIsUploadingUsers] = useState(false);
  const [onboardingSchema, setOnboardingSchema] = useState<Partial<OnboardingSchemaMap>>(getFallbackOnboardingSchema());
  const userUploadInputRef = useRef<HTMLInputElement | null>(null);
  const [dbRoles, setDbRoles] = useState<Array<{ value: string; label: string }>>([]);

  const activeCompanyId =
    currentUser?.companyId ||
    Number(sessionStorage.getItem('company_id') || localStorage.getItem('company_id') || 0) ||
    null;
  const activeSiteId =
    currentUser?.siteId ||
    Number(sessionStorage.getItem('site_id') || localStorage.getItem('site_id') || 0) ||
    null;
  const activeSiteCode =
    currentUser?.siteCode ||
    sessionStorage.getItem('tenant') ||
    localStorage.getItem('currentSite') ||
    null;
  const selectedSite = sites.find((site) => String(site.site_id) === siteFilter);

  const fetchUsers = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }

      let response;
      if (isSystemAdmin) {
        const requestedCompanyId = companyFilter !== 'all' ? companyFilter : null;
        const requestedSiteId = siteFilter !== 'all' ? siteFilter : null;
        const requestedSiteCode = requestedSiteId ? selectedSite?.site_code || null : null;
        response = await backendApi.getUsers(requestedCompanyId, requestedSiteId, requestedSiteCode, { skipStorageFallback: true });
      } else if (isHoAdmin) {
        const requestedCompanyId = companyFilter !== 'all' ? companyFilter : (activeCompanyId ? String(activeCompanyId) : null);
        const requestedSiteId = siteFilter !== 'all' ? siteFilter : null;
        const requestedSiteCode = requestedSiteId ? selectedSite?.site_code || null : null;
        response = await backendApi.getUsers(requestedCompanyId, requestedSiteId, requestedSiteCode, { skipStorageFallback: true });
      } else {
        response = await backendApi.getUsers(activeCompanyId, activeSiteId, activeSiteCode);
      }
      if (response.success) {
        const usersList = response.users || [];
        setUsers(usersList || []);
      } else {
        console.error('Failed to fetch users:', response.error);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const storedCompany = localStorage.getItem('admin_users_company_filter');
    const storedSite = localStorage.getItem('admin_users_site_filter');

    if (isSystemAdmin) {
      if (!storedCompany) setCompanyFilter('all');
      if (!storedSite) setSiteFilter('all');
      setScopeFiltersReady(true);
      return;
    }

    if (isHoAdmin) {
      if (!storedCompany) setCompanyFilter(activeCompanyId ? String(activeCompanyId) : 'all');
      if (!storedSite) setSiteFilter('all');
      setScopeFiltersReady(true);
      return;
    }

    if (!storedCompany) setCompanyFilter(activeCompanyId ? String(activeCompanyId) : 'all');
    if (!storedSite) setSiteFilter(activeSiteId ? String(activeSiteId) : 'all');
    setScopeFiltersReady(true);
  }, [isSystemAdmin, isHoAdmin, activeCompanyId, activeSiteId]);

  useEffect(() => {
    // Load roles from dropdown_values table
    backendApi.getDropdowns('roles').then((res: any) => {
      if (res.success && Array.isArray(res.values) && res.values.length > 0) {
        setDbRoles(res.values);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadCompanies = async () => {
      if (!isSystemAdmin) {
        if (mounted) {
          setCompanies([]);
        }
        return;
      }

      setLoadingScopeFilters(true);
      const response = await backendApi.getCompanies();
      if (!mounted) {
        return;
      }

      if (response.success && Array.isArray(response.companies)) {
        setCompanies(response.companies);
      } else {
        setCompanies([]);
      }
      setLoadingScopeFilters(false);
    };

    loadCompanies();

    return () => {
      mounted = false;
    };
  }, [isSystemAdmin]);

  useEffect(() => {
    let mounted = true;

    const loadSites = async () => {
      if (isSiteAdmin) {
        if (mounted) {
          setSites(activeSiteId && activeSiteCode ? [{
            site_id: activeSiteId,
            site_name: activeSiteCode,
            site_code: activeSiteCode,
            company_id: activeCompanyId,
          }] : []);
        }
        return;
      }

      const effectiveCompanyId = isSystemAdmin
        ? (companyFilter !== 'all' ? companyFilter : null)
        : (activeCompanyId ? String(activeCompanyId) : null);

      if (!effectiveCompanyId) {
        if (mounted) {
          setSites([]);
          setSiteFilter('all');
        }
        return;
      }

      setLoadingScopeFilters(true);
      const response = await backendApi.getSites(false, '', effectiveCompanyId);
      if (!mounted) {
        return;
      }

      if (response.success && Array.isArray(response.sites)) {
        setSites(response.sites);
        setSiteFilter((prev) => (
          prev !== 'all' && !response.sites.some((site: SiteOption) => String(site.site_id) === prev)
            ? 'all'
            : prev
        ));
      } else {
        setSites([]);
        setSiteFilter('all');
      }
      setLoadingScopeFilters(false);
    };

    loadSites();

    return () => {
      mounted = false;
    };
  }, [isSystemAdmin, isHoAdmin, isSiteAdmin, companyFilter, activeCompanyId, activeSiteId, activeSiteCode]);

  useEffect(() => {
    fetchUsers(true);
  }, [currentUser?.companyId, currentUser?.siteId, currentUser?.siteCode]);

  useEffect(() => {
    if (!scopeFiltersReady) {
      return;
    }

    if (!hasInitializedScopedFilterFetch.current) {
      hasInitializedScopedFilterFetch.current = true;
      return;
    }

    fetchUsers(false);
  }, [companyFilter, siteFilter, scopeFiltersReady]);

  useEffect(() => {
    startRealTimePolling();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [currentUser?.companyId, currentUser?.siteId, currentUser?.siteCode, companyFilter, siteFilter]);

  useEffect(() => {
    const loadOnboardingSchema = async () => {
      const response = await backendApi.getAdminOnboardingSchema();
      if (response.success && response.schema) {
        setOnboardingSchema(response.schema);
      }
    };

    void loadOnboardingSchema();
  }, []);

  const startRealTimePolling = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      fetchUsers(false);
    }, POLL_INTERVAL);
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setFeedbackToast({ type, message });
    setTimeout(() => setFeedbackToast(null), 4000);
  };

  const handleDelete = async (userId: number) => {
    const userToDelete = users.find(u => u.id === userId);
    if (userToDelete?.username === currentUser?.username) {
      showToast('You cannot delete your own account', 'error');
      return;
    }

    if (window.confirm(`Are you sure you want to permanently delete user "${userToDelete?.username || userId}"? This action cannot be undone.`)) {
      try {
        const response = await backendApi.deleteUser(userId);
        if (response.success) {
          showToast(`User @${userToDelete?.username || ''} deleted successfully`, 'success');
          fetchUsers(false);
        } else {
          showToast(response.error || 'Failed to delete user', 'error');
        }
      } catch (error: any) {
        showToast(error.message || 'Failed to delete user', 'error');
      }
    }
  };

  const handleToggleActive = async (userId: number, isActive: boolean) => {
    try {
      const response = await backendApi.activateUser(userId, !isActive);
      if (response.success) {
        setUsers(users.map(u => u.id === userId ? { ...u, is_active: !isActive } : u));
        showToast(`User account ${!isActive ? 'activated' : 'deactivated'} successfully`, 'success');
      } else {
        showToast(response.error || 'Failed to update user status', 'error');
      }
    } catch (error: any) {
      showToast(error.message || 'Failed to update user status', 'error');
    }
  };

  const handleUnlock = async (userId: number) => {
    if (window.confirm('Are you sure you want to unlock this account?')) {
      try {
        const response = await backendApi.unlockUser(userId);
        if (response.success) {
          fetchUsers(false);
          showToast('Account unlocked successfully', 'success');
        } else {
          showToast(response.error || 'Failed to unlock account', 'error');
        }
      } catch (error: any) {
        showToast(error.message || 'Failed to unlock account', 'error');
      }
    }
  };

  const handleOpenResetModal = (targetUser: User) => {
    setResetModal({ isOpen: true, userId: targetUser.id, username: targetUser.username });
    setResetPassword('');
    setResetConfirmPassword('');
    setResetError(null);
    setShowResetPwd(false);
  };

  const handleConfirmResetPassword = async () => {
    if (!resetPassword || resetPassword.length < 6) {
      setResetError('Password must be at least 6 characters long');
      return;
    }
    if (resetPassword !== resetConfirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    if (!resetModal.userId) return;

    try {
      setResetLoading(true);
      setResetError(null);
      const response = await backendApi.resetUserPassword(resetModal.userId, resetPassword);
      if (response.success) {
        const targetName = resetModal.username;
        setResetModal({ isOpen: false, userId: null, username: '' });
        showToast(`Password for @${targetName} has been reset successfully.`, 'success');
      } else {
        setResetError(response.error || response.message || 'Failed to reset password');
      }
    } catch (error: any) {
      setResetError(error.message || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  const isAccountLocked = (user: User) => {
    if (!user.account_locked_until) return false;
    return new Date(user.account_locked_until) > new Date();
  };

  const resolveCompanyIdFromRow = (row: Record<string, any>): string | null => {
    const companyCode = String(pickRowValue(row, ['company_code'])).trim().toUpperCase();
    if (companyCode) {
      const company = companies.find((item) => String(item.company_code || '').toUpperCase() === companyCode);
      if (company) {
        return String(company.company_id);
      }
    }

    const companyName = String(pickRowValue(row, ['company_name'])).trim().toLowerCase();
    if (companyName) {
      const company = companies.find((item) => String(item.company_name || '').trim().toLowerCase() === companyName);
      if (company) {
        return String(company.company_id);
      }
    }

    // Backward-compatible fallback for older internal sheets that still carry DB IDs.
    const directCompanyId = String(pickRowValue(row, ['company_id'])).trim();
    if (directCompanyId) {
      return directCompanyId;
    }

    if (!isSystemAdmin && activeCompanyId) {
      return String(activeCompanyId);
    }

    return null;
  };

  const handleUserUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canCreateUser) {
      alert('You are not authorized to bulk onboard users.');
      return;
    }

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setIsUploadingUsers(true);
    try {
      const rows = await parseExcelRows(file);
      if (rows.length === 0) {
        alert('No data rows found in uploaded Excel file.');
        return;
      }

      const siteCache = new Map<string, SiteOption[]>();
      const getSitesByCompany = async (companyId: string | null): Promise<SiteOption[]> => {
        if (!companyId) {
          return [];
        }

        if (siteCache.has(companyId)) {
          return siteCache.get(companyId) || [];
        }

        const response = await backendApi.getSites(false, '', companyId);
        const resolvedSites = response.success && Array.isArray(response.sites) ? response.sites : [];
        siteCache.set(companyId, resolvedSites);
        return resolvedSites;
      };

      const failures: string[] = [];
      let successCount = 0;
      let actionableRows = 0;

      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        const rowNumber = index + 2;

        if (isDemoTemplateRow('users', row, onboardingSchema)) {
          continue;
        }

        actionableRows += 1;

        const schemaErrors = validateOnboardingRow('users', row, onboardingSchema);
        if (schemaErrors.length > 0) {
          failures.push(`Row ${rowNumber}: ${schemaErrors.join('; ')}`);
          continue;
        }

        const username = String(pickRowValue(row, ['username'])).trim();
        const email = String(pickRowValue(row, ['email'])).trim();
        const password = String(pickRowValue(row, ['password'])).trim();

        if (!username || !email || !password) {
          failures.push(`Row ${rowNumber}: username, email and password are required`);
          continue;
        }

        const rawRole = String(pickRowValue(row, ['role'])).trim().toLowerCase().replace(/\s+/g, '_');
        const role = rawRole || 'general_user';
        const roleRequiresSite = ['site_admin', 'general_user', 'user'].includes(role);
        const roleRequiresCompany = role !== 'system_admin';

        const companyId = resolveCompanyIdFromRow(row);
        if (roleRequiresCompany && !companyId) {
          failures.push(`Row ${rowNumber}: company_name mapping is required for role ${role}`);
          continue;
        }

        let siteId = '';
        if (roleRequiresSite) {
          const siteCode = String(pickRowValue(row, ['site_code'])).trim().toUpperCase();
          const siteName = String(pickRowValue(row, ['site_name'])).trim().toLowerCase();

          const scopedSites = companyId ? await getSitesByCompany(companyId) : sites;
          const matchedSite = scopedSites.find((site) => {
            const byCode = siteCode && String(site.site_code || '').toUpperCase() === siteCode;
            const byName = siteName && String(site.site_name || '').trim().toLowerCase() === siteName;
            return byCode || byName;
          });

          if (matchedSite) {
            siteId = String(matchedSite.site_id);
          } else {
            // Backward-compatible fallback for older internal sheets that still carry DB IDs.
            siteId = String(pickRowValue(row, ['site_id'])).trim();
          }

          if (siteId) {
            const matchedById = scopedSites.find((site) => String(site.site_id) === siteId);
            if (matchedById) {
              siteId = String(matchedById.site_id);
            }
          } else if (!isSystemAdmin && activeSiteId) {
            siteId = String(activeSiteId);
          }
        }

        if (roleRequiresSite && !siteId) {
          failures.push(`Row ${rowNumber}: site_name mapping is required for role ${role}`);
          continue;
        }

        const payload = {
          username,
          email,
          password,
          first_name: String(pickRowValue(row, ['first_name'])).trim(),
          last_name: String(pickRowValue(row, ['last_name'])).trim(),
          role,
          site_id: roleRequiresSite ? siteId : null,
          is_active: parseBooleanCell(pickRowValue(row, ['is_active']), true),
          country_code: String(pickRowValue(row, ['country_code'])).trim() || null,
          phone_number: String(pickRowValue(row, ['phone_number'])).trim() || null,
          company_id: roleRequiresCompany ? companyId : null,
        };

        const response = await backendApi.createUser(payload);
        if (response.success) {
          successCount += 1;
        } else {
          failures.push(`Row ${rowNumber}: ${response.error || 'create failed'}`);
        }
      }

      await fetchUsers(false);

      if (actionableRows === 0) {
        alert('No actionable rows found. Replace or delete the demo row in the template and upload again.');
        return;
      }

      const summary = [
        'User upload complete.',
        `Success: ${successCount}`,
        `Failed: ${failures.length}`,
      ];
      if (failures.length > 0) {
        summary.push('', 'First errors:', ...failures.slice(0, 6));
      }
      alert(summary.join('\n'));
    } catch (error: any) {
      alert(error?.message || 'Failed to process user upload file');
    } finally {
      setIsUploadingUsers(false);
      event.target.value = '';
    }
  };

  const filteredUsers = users.filter(user => {
    if (isSelfUser(user)) {
      const matchesSearch =
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());

      const normalizedSelfRole = normalizeRole(user.role_name || user.role || '', !!user.site?.site_id);
      const matchesRole = roleFilter === 'all' || normalizedSelfRole === roleFilter || user.role === roleFilter || user.role_name === roleFilter;
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && user.is_active) ||
        (statusFilter === 'inactive' && !user.is_active);

      return matchesSearch && matchesRole && matchesStatus;
    }

    const matchesSearch =
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());

    const userCompanyId = String(user.company_id || user.site?.company_id || '');
    const userSiteId = String(user.site?.site_id || '');
    const matchesCompany = companyFilter === 'all' || userCompanyId === companyFilter;
    const matchesSite = siteFilter === 'all' || userSiteId === siteFilter;

    const normalizedUserRole = normalizeRole(user.role_name || user.role || '', !!user.site?.site_id);
    const matchesRole = roleFilter === 'all' || normalizedUserRole === roleFilter || user.role === roleFilter || user.role_name === roleFilter;
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);

    return matchesSearch && matchesCompany && matchesSite && matchesRole && matchesStatus;
  });

  const getRoleBadgeColor = (role: string) => {
    switch (role?.toLowerCase()) {
      case 'system_admin': return 'bg-[#E3F0F9] text-[#1C4D88] border-[#C2DFF1]';
      case 'ho_admin':
      case 'company_admin': return 'bg-[#E3F0F9] text-[#1C4D88] border-[#C2DFF1]';
      case 'site_admin': return 'bg-[#E3F0F9] text-[#1C4D88] border-[#C2DFF1]';
      case 'general_user': return 'bg-green-100 text-green-700 border-green-200';
      case 'admin': return 'bg-[#E3F0F9] text-[#1C4D88] border-[#C2DFF1]';
      case 'user': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'viewer': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-shell">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 lg:pt-4 pb-8 px-2 sm:px-3 lg:px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <motion.section initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="admin-hero p-4 sm:p-4 lg:p-5">
          <div className="admin-toolbar">
            <div className="space-y-2">
              <p className="admin-eyebrow">User administration</p>
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-11 h-11 rounded-[16px] bg-[linear-gradient(135deg,#0084CF,#1C4D88)] items-center justify-center text-white shadow-[0_12px_20px_rgba(0,132,207,0.2)]">
                  <Users size={22} />
                </div>
                <div>
                  <h1 className="admin-page-title">People, roles, and account state in one disciplined row layout.</h1>
                  <p className="admin-page-subtitle mt-2">The interface now prioritizes identity clarity, role visibility, and direct actions without the visual weight of an old-style table grid.</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {canCreateUser && (
                <>
                  <input
                    ref={userUploadInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                    onChange={handleUserUpload}
                  />
                  <button
                    onClick={() => userUploadInputRef.current?.click()}
                    className="admin-button admin-button-secondary"
                    disabled={isUploadingUsers}
                  >
                    <Upload size={16} />
                    {isUploadingUsers ? 'Uploading...' : 'Upload Data'}
                  </button>
                </>
              )}
              {canCreateUser && (
                <button onClick={() => navigate('/admin/users/create')} className="admin-button admin-button-primary">
                  <Plus size={18} />
                  Create user
                </button>
              )}
            </div>
          </div>

          <div className="admin-stat-strip mt-6">
            <div className="admin-stat-card">
              <p className="admin-stat-label">Users</p>
              <p className="admin-stat-value">{filteredUsers.length}</p>
              <p className="admin-stat-hint">Accounts matching the active scope and filters</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Active</p>
              <p className="admin-stat-value">{filteredUsers.filter((item) => item.is_active).length}</p>
              <p className="admin-stat-hint">Currently enabled accounts</p>
            </div>
            <div className="admin-stat-card">
              <p className="admin-stat-label">Locked</p>
              <p className="admin-stat-value">{filteredUsers.filter((item) => isAccountLocked(item)).length}</p>
              <p className="admin-stat-hint">Accounts requiring unlock action</p>
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">Filter users</h2>
              <p className="admin-section-copy">Search by name, username, or email and narrow by customer company, site, role, or status.</p>
            </div>
          </div>
          <div className="admin-section-body">
            <div className="flex flex-col gap-4">
              {/* Row 1: Search & Reset */}
              <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
                <div className="admin-search-wrap w-full md:max-w-md">
                  <Search size={18} />
                  <input
                    type="text"
                    placeholder="Search users by name, email, or username..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="admin-input"
                  />
                </div>
                
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setRoleFilter('all');
                    setStatusFilter('all');
                    setCompanyFilter(isSystemAdmin ? 'all' : (activeCompanyId ? String(activeCompanyId) : 'all'));
                    setSiteFilter(isSiteAdmin && activeSiteId ? String(activeSiteId) : 'all');
                  }}
                  className="admin-button admin-button-ghost w-full md:w-auto xl:h-[46px] xl:px-4 xl:whitespace-nowrap"
                >
                  <Filter size={16} />
                  Reset filters
                </button>
              </div>

              {/* Row 2: Select Filters with clean labels */}
              <div className="flex flex-wrap gap-3 pt-3 border-t border-[#1C4D88]/5">
                {isSystemAdmin && (
                  <div className="w-full sm:w-auto min-w-[200px] flex-1">
                    <label className="block text-[10px] font-bold text-[#6A7A92] uppercase tracking-[0.16em] mb-1.5">
                      Company
                    </label>
                    <select
                      title="Customer Company Filter"
                      value={companyFilter}
                      onChange={(e) => {
                        setCompanyFilter(e.target.value);
                        setSiteFilter('all');
                      }}
                      className="admin-select w-full"
                      disabled={loadingScopeFilters}
                    >
                      <option value="all">All customer companies</option>
                      {companies.map((company) => (
                        <option key={company.company_id} value={company.company_id}>
                          {company.company_name} {company.company_code ? `(${company.company_code})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {(isSystemAdmin || isHoAdmin) && (
                  <div className="w-full sm:w-auto min-w-[190px] flex-1">
                    <label className="block text-[10px] font-bold text-[#6A7A92] uppercase tracking-[0.16em] mb-1.5">
                      Site
                    </label>
                    <select
                      title="Site Filter"
                      value={siteFilter}
                      onChange={(e) => setSiteFilter(e.target.value)}
                      className="admin-select w-full"
                      disabled={loadingScopeFilters || (isSystemAdmin && companyFilter === 'all')}
                    >
                      <option value="all">{isSystemAdmin && companyFilter === 'all' ? 'Select company first' : 'All sites'}</option>
                      {sites.map((site) => (
                        <option key={site.site_id} value={site.site_id}>
                          {site.site_name} ({site.site_code})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="w-full sm:w-auto min-w-[150px] flex-1">
                  <label className="block text-[10px] font-bold text-[#6A7A92] uppercase tracking-[0.16em] mb-1.5">
                    Role
                  </label>
                  <select
                    title="Role Filter"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="admin-select w-full"
                  >
                    <option value="all">All roles</option>
                    {(dbRoles.length > 0 ? dbRoles : [
                      { value: 'system_admin', label: 'System Admin' },
                      { value: 'ho_admin', label: 'Ho Admin' },
                      { value: 'company_admin', label: 'Company Admin' },
                      { value: 'site_admin', label: 'Site Admin' },
                      { value: 'general_user', label: 'General User' },
                    ]).map((role) => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>

                <div className="w-full sm:w-auto min-w-[140px] flex-1">
                  <label className="block text-[10px] font-bold text-[#6A7A92] uppercase tracking-[0.16em] mb-1.5">
                    Status
                  </label>
                  <select
                    title="Status Filter"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="admin-select w-full"
                  >
                    <option value="all">All status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {(isSystemAdmin || isHoAdmin) && companyFilter !== 'all' && (
                <span className="admin-pill admin-pill-info">Company scoped</span>
              )}
              {(isSystemAdmin || isHoAdmin) && siteFilter !== 'all' && selectedSite && (
                <span className="admin-pill admin-pill-info">Site: {selectedSite.site_name}</span>
              )}
            </div>
          </div>
        </motion.section>

        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2 className="admin-section-title">User directory</h2>
              <p className="admin-section-copy">Entries are arranged as rows with identity, scope, status, and account actions aligned side by side.</p>
            </div>
          </div>
          <div className="admin-section-body">
            {filteredUsers.length === 0 ? (
              <div className="admin-empty">No users matched the current filters.</div>
            ) : (
              <div className="admin-row-list">
                {filteredUsers.map((item, index) => (
                  <motion.div key={item.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index, 8) * 0.035 }} className="admin-row">
                    <div className="admin-row-leading">
                      <div className="admin-row-icon rounded-full overflow-hidden">
                        {item.profile_picture ? (
                          <img src={item.profile_picture} alt={item.username} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-sm font-semibold">{item.username[0]?.toUpperCase()}</span>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="admin-row-title truncate">{item.first_name} {item.last_name}</p>
                        <p className="admin-row-subtitle truncate">{item.email || item.username}</p>
                        <div className="admin-row-tags">
                          <span className={`admin-pill ${item.is_active ? 'admin-pill-success' : 'admin-pill-muted'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
                          {isAccountLocked(item) && <span className="admin-pill admin-pill-danger">Locked</span>}
                        </div>
                      </div>
                    </div>

                    <div className="admin-row-meta">
                      {(currentUser?.role === 'super_admin' || currentUser?.isGlobalAdmin || isSystemAdmin) && (
                        <div className="admin-meta-block">
                          <p className="admin-meta-label">Customer</p>
                          <p className="admin-meta-value">{item.company_name || item.site?.company_name || 'N/A'}</p>
                        </div>
                      )}
                      <div className="admin-meta-block">
                        <p className="admin-meta-label">Role</p>
                        <p className="admin-meta-value">{item.role_display || (item.role_name ? item.role_name.replace('_', ' ') : item.role)}</p>
                      </div>
                      <div className="admin-meta-block">
                        <p className="admin-meta-label">Site</p>
                        <p className="admin-meta-value">{item.site?.site_name || 'No site assigned'}</p>
                      </div>
                      <div className="admin-meta-block">
                        <p className="admin-meta-label">Phone</p>
                        <p className="admin-meta-value">{item.phone_number || 'Not available'}</p>
                      </div>
                    </div>

                    <div className="admin-row-actions">
                      {canToggleUserStatus(item) ? (
                        <button onClick={() => handleToggleActive(item.id, item.is_active)} className={`admin-pill ${item.is_active ? 'admin-pill-success' : 'admin-pill-muted'}`}>
                          {item.is_active ? 'Active' : 'Inactive'}
                        </button>
                      ) : (
                        <span className={`admin-pill ${item.is_active ? 'admin-pill-success' : 'admin-pill-muted'}`}>{item.is_active ? 'Active' : 'Inactive'}</span>
                      )}
                      <button onClick={() => navigate(`/admin/users/${item.id}`)} className="admin-icon-button" title="View user" aria-label="View user">
                        <Eye size={16} />
                      </button>
                      {canViewOrEditUser(item) && (
                        <button onClick={() => navigate(`/admin/users/${item.id}/edit`)} className="admin-icon-button" title="Edit user" aria-label="Edit user">
                          <Edit size={16} />
                        </button>
                      )}
                      {canUnlockUser(item) && (
                        <button onClick={() => handleUnlock(item.id)} className="admin-icon-button text-[#9F6B00]" title="Unlock account" aria-label="Unlock account">
                          <Unlock size={16} />
                        </button>
                      )}
                      {canResetPasswordForUser(item) && (
                        <button onClick={() => handleOpenResetModal(item)} className="admin-icon-button" title="Reset password" aria-label="Reset password">
                          <Key size={16} />
                        </button>
                      )}
                      {canDeleteUser(item) && item.username !== currentUser?.username ? (
                        <button onClick={() => handleDelete(item.id)} className="admin-icon-button text-[#B5475D]" title="Delete user" aria-label="Delete user">
                          <Trash2 size={16} />
                        </button>
                      ) : (
                        <div className="admin-action-placeholder" />
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.section>
      </div>
      {/* Deletion Progress Popup */}
      <DeletionProgressPopup
        url={deleteStreamUrl || ''}
        isOpen={!!deleteStreamUrl}
        onClose={() => setDeleteStreamUrl(null)}
        onSuccess={() => fetchUsers(false)}
      />

      {/* Modern Reset Password Modal */}
      {resetModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-2">
                <Key className="text-[#0066A3]" size={18} />
                <h3 className="text-base font-semibold text-gray-900">Reset User Password</h3>
              </div>
              <button
                type="button"
                onClick={() => setResetModal({ isOpen: false, userId: null, username: '' })}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Set a new password for <span className="font-semibold text-gray-900">@{resetModal.username}</span>.
              </p>

              {resetError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs flex items-center gap-2">
                  <XCircle size={15} className="shrink-0 text-red-500" />
                  <span>{resetError}</span>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">New Password</label>
                  <div className="relative">
                    <input
                      type={showResetPwd ? 'text' : 'password'}
                      value={resetPassword}
                      onChange={(e) => {
                        setResetPassword(e.target.value);
                        setResetError(null);
                      }}
                      placeholder="Min 6 characters"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0084CF] focus:border-transparent pr-16 bg-white text-gray-900"
                    />
                    <button
                      type="button"
                      onClick={() => setShowResetPwd(!showResetPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-gray-500 hover:text-gray-700"
                    >
                      {showResetPwd ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Confirm New Password</label>
                  <input
                    type={showResetPwd ? 'text' : 'password'}
                    value={resetConfirmPassword}
                    onChange={(e) => {
                      setResetConfirmPassword(e.target.value);
                      setResetError(null);
                    }}
                    placeholder="Repeat password"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#0084CF] focus:border-transparent bg-white text-gray-900"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setResetModal({ isOpen: false, userId: null, username: '' })}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={resetLoading}
                onClick={handleConfirmResetPassword}
                className="px-4 py-2 text-sm font-semibold text-white bg-[#0084CF] hover:bg-[#0043CE] rounded-lg transition disabled:opacity-50"
              >
                {resetLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Feedback Toast */}
      {feedbackToast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-in slide-in-from-bottom-3 duration-200 text-white ${
          feedbackToast.type === 'error' ? 'bg-rose-600' : 'bg-emerald-600'
        }`}>
          {feedbackToast.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
          <span>{feedbackToast.message}</span>
          <button type="button" onClick={() => setFeedbackToast(null)} className="ml-2 text-white/80 hover:text-white">
            <X size={15} />
          </button>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
